// Pick the "best display unit" for a numeric amount so the displayed value
// reads in [1, 1000) whenever the chain allows it. The rule:
//   - Promote up (smaller unit → larger) takes priority. Walk from the
//     largest unit in the chain down and pick the first one where the
//     displayed value is ≥ 1.
//   - That naturally also handles the "promote down" direction: when the
//     value at the current unit is < 1, the largest-where-≥1 search will
//     land on a smaller unit instead.
//   - At chain boundaries we keep the chain endpoint even if the value
//     overflows/underflows (e.g. 5000 ppm stays as 5000 ppm — there is no
//     bigger flavoring unit; 0.1 ppt stays as 0.1 ppt).
//
// Used at three points in the formulation lifecycle to keep saved + shown
// units coherent: initial DTO → Variant translation, autosave serialization,
// and on-blur after the user edits a Conc input.
//
// Chains are per unit family (flavoring / mass / volume). Each step is a
// fixed 1000× factor between adjacent units. We never cross families — a
// row saved in mg never normalizes into ppm.

import type { AmountUnit } from "@/services/formula_api";

type ChainStep = { unit: AmountUnit; factor: number };
type Chain = readonly ChainStep[];

// Smallest to largest. `factor` is "how many of this unit equal one
// chain-base unit" — i.e. the ratio used when converting across the chain.
const FLAVORING_CHAIN: Chain = [
  { unit: "ppt", factor: 1 },
  { unit: "ppb", factor: 1000 },
  { unit: "ppm", factor: 1_000_000 },
];

const MASS_CHAIN: Chain = [
  { unit: "mg", factor: 1 },
  { unit: "g", factor: 1000 },
  { unit: "kg", factor: 1_000_000 },
];

const VOLUME_CHAIN: Chain = [
  { unit: "μL", factor: 1 },
  { unit: "mL", factor: 1000 },
  { unit: "L", factor: 1_000_000 },
];

function chainFor(unit: AmountUnit): Chain | null {
  if (unit === "ppt" || unit === "ppb" || unit === "ppm") return FLAVORING_CHAIN;
  if (unit === "mg" || unit === "g" || unit === "kg") return MASS_CHAIN;
  if (unit === "μL" || unit === "mL" || unit === "L") return VOLUME_CHAIN;
  return null;
}

// Cross-family ordering when sorting rows in the flask: volume rows sort
// above mass rows, which sort above parts-per rows, with unknown units last.
// Inside a family we fall through to base-unit-equivalent value (smallest
// unit in chain × factor) so e.g. "1 ppm" (= 1000 ppb) sorts above "500 ppb".
const FAMILY_PRIORITY: Record<string, number> = {
  volume: 0,
  mass: 1,
  partsPer: 2,
  unknown: 3,
};

function familyOf(unit: AmountUnit | undefined): keyof typeof FAMILY_PRIORITY {
  if (!unit) return "unknown";
  if (unit === "μL" || unit === "mL" || unit === "L") return "volume";
  if (unit === "mg" || unit === "g" || unit === "kg") return "mass";
  if (unit === "ppt" || unit === "ppb" || unit === "ppm") return "partsPer";
  return "unknown";
}

/**
 * Sort key for "decreasing concentration" ordering inside a flask group.
 *
 * Returns a tuple `[familyRank, -baseValue]`. Sort the rows by this tuple
 * ascending and you get: volume rows first, then mass, then parts-per,
 * then unknowns — and within each family, rows are ordered by descending
 * magnitude in the chain's base unit.
 *
 * Used by FlaskPanel to sort each lens-bucketed group. Lives next to the
 * chain definitions so the family/base-unit tables stay in one place.
 */
export function amountSortKey(
  value: number,
  unit: AmountUnit | undefined,
): [number, number] {
  const family = familyOf(unit);
  const familyRank = FAMILY_PRIORITY[family];
  if (family === "unknown" || !unit) return [familyRank, 0];
  const chain = chainFor(unit);
  if (!chain) return [familyRank, 0];
  const step = chain.find((s) => s.unit === unit);
  if (!step) return [familyRank, 0];
  // Negative so "ascending sort by key" yields "descending by value" within
  // the family.
  return [familyRank, -value * step.factor];
}

export type NormalizedAmount = { value: number; unit: AmountUnit };

/**
 * Re-express `(value, unit)` in the most reader-friendly unit within its
 * chain (ppm/ppb/ppt, mg/g/kg, or μL/mL/L). Idempotent — calling it on a
 * value that's already normalized returns the input.
 *
 * Returns the input unchanged when:
 *  - value is exactly 0 (no magnitude to balance — keep the saved unit so
 *    "not decided yet" rows don't migrate units on each save round-trip)
 *  - value is NaN/Infinity
 *  - unit is outside the three known chains
 */
export function normalizeAmountUnit(
  value: number,
  unit: AmountUnit,
): NormalizedAmount {
  if (value === 0 || !Number.isFinite(value)) return { value, unit };
  const chain = chainFor(unit);
  if (!chain) return { value, unit };

  const currentStep = chain.find((s) => s.unit === unit);
  if (!currentStep) return { value, unit };

  // Project onto the chain's base unit so we can re-express in any step.
  const baseValue = value * currentStep.factor;

  // Walk largest → smallest. Pick the first step where |displayed| ≥ 1 —
  // i.e. the largest unit that doesn't force the value below 1. If none
  // qualify (value is smaller than the smallest unit can express as ≥ 1),
  // fall back to the smallest unit so the value at least stays in-chain.
  for (let i = chain.length - 1; i >= 0; i--) {
    const displayed = baseValue / chain[i].factor;
    if (Math.abs(displayed) >= 1) {
      return { value: displayed, unit: chain[i].unit };
    }
  }
  const smallest = chain[0];
  return { value: baseValue / smallest.factor, unit: smallest.unit };
}
